<template>
  <div>
    <div class="oficina-tabs">
      <button
        v-for="tab in visibleTabs"
        :key="tab.id"
        class="oficina-tab"
        :class="{ active: activeTab === tab.id }"
        @click="updateTab(tab.id)"
      >
        <span :class="getIconClass(tab.icon)"></span> {{ tab.label }}
      </button>
    </div>
    <div class="oficina-tab-content">
      <!-- Tab WhatsApp -->
      <div v-if="activeTab === 'whatsapp'">
        <div v-if="config.whatsappNumber" class="qr-section">
          <div class="qr-container">
            <div class="oficina-qr-code">
              <img :src="qrCodeUrl" alt="QR Code WhatsApp" />
            </div>
            <div class="qr-info">
              <div class="qr-header">
                <p>Suporte eSUS Feedback</p>
                <button type="button" @click="copyWhatsappLink" class="copy-btn" title="Copiar link do WhatsApp">
                  <span class="oficina-icon-copy"></span>
                </button>
              </div>
              <p class="qr-number">{{ formattedWhatsapp }}</p>
            </div>
          </div>
        </div>
        <div v-else class="oficina-slide-text">
          Número do WhatsApp não configurado.
        </div>
      </div>

      <!-- Tab Google Meet -->
      <div v-else-if="activeTab === 'google-meet'">
        <h3 class="tech-setup-title">Verifique os elementos do Google Meet:</h3>
        <div class="tech-setup-cards">
          <div class="tech-setup-card-item" v-for="(item, idx) in currentTab.items" :key="idx">
            <strong class="tech-setup-card-title">{{ item.split(':')[0] }}</strong>
            <p class="tech-setup-card-text">{{ item.split(':').slice(1).join(':').trim() }}</p>
          </div>
        </div>
      </div>

      <!-- Tab Suporte -->
      <div v-else-if="activeTab === 'suporte'">
        <h3 class="tech-setup-title">Verifique com sua equipe:</h3>
        <div class="tech-setup-cards">
          <div class="tech-setup-card-item" v-for="(item, idx) in currentTab.items" :key="idx">
            <strong class="tech-setup-card-title">{{ item.split(':')[0] }}</strong>
            <p class="tech-setup-card-text">{{ item.split(':').slice(1).join(':').trim() }}</p>
          </div>
        </div>
      </div>

      <!-- Tab Sistemas -->
      <div v-else-if="activeTab === 'sistemas'">
        <h3 class="tech-setup-title">Verifique os links importantes:</h3>
        <div class="tech-setup-cards">
          <div class="tech-setup-card-item">
            <strong class="tech-setup-card-title">Link de Avaliação</strong>
            <div class="tech-setup-link-container">
              <span class="tech-setup-link-value">{{ config.evaluationLink }}</span>
              <button
                type="button"
                class="tech-setup-link-btn"
                @click="copyEvaluationLink"
                title="Copiar link"
              >
                <span class="oficina-icon-copy"></span>
              </button>
            </div>
          </div>
          <div class="tech-setup-card-item">
            <strong class="tech-setup-card-title">Plataforma de Transmissão</strong>
            <p class="tech-setup-card-text">{{ config.platform }} - Verifique se está funcionando corretamente.</p>
          </div>
        </div>
      </div>

      <!-- Tab Acesso Remoto -->
      <div v-else-if="activeTab === 'acesso-remoto'">
        <h3 class="tech-setup-title">Plano B: Acesso Remoto</h3>
        <p class="tech-setup-intro-text">Se houver problemas no computador da sala, você pode precisar acessá-lo remotamente.</p>
        <div class="tech-setup-cards">
          <div class="tech-setup-card-item">
            <p class="tech-setup-card-text" style="margin-bottom: 12px;">Envie este link para teste agora:</p>
            <div class="tech-setup-link-container">
              <span class="tech-setup-link-value">{{ config.sosLink }}</span>
              <button
                type="button"
                class="tech-setup-link-btn"
                @click="copySosLink"
                title="Copiar link"
              >
                <span class="oficina-icon-copy"></span>
              </button>
            </div>
            <p class="tech-setup-card-text" style="margin-top: 16px;">
              Instrução: <strong>"Acesse esse link se precisarmos mexer no computador à distância."</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-if="step.tip" class="oficina-slide-tip">
      <i class="icofont-info-circle"></i> {{ step.tip }}
    </div>
  </div>
</template>

<script>
import { getQRCodeUrl, getWhatsappUrl, formatPhoneNumber, copyToClipboard } from '../../utils'

export default {
  name: 'TechnicalSetupSlide',
  props: {
    step: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
    activeTabs: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    QRCodeSection: {
      props: ['whatsappNumber'],
      methods: {
        getQRCodeUrl,
        getWhatsappUrl,
        formatPhoneNumber,
        async copyLink() {
          const link = this.getWhatsappUrl(this.whatsappNumber)
          await copyToClipboard(link)
          if (this.$toast) {
            this.$toast.success('Link copiado!', 'Sucesso', { position: 'topRight' })
          }
        },
      },
      template: `
        <div class="qr-section">
          <div class="qr-container">
            <div class="oficina-qr-code">
              <img :src="getQRCodeUrl(getWhatsappUrl(whatsappNumber))" alt="QR Code WhatsApp" />
            </div>
            <div class="qr-info">
              <div class="qr-header">
                <p>Suporte eSUS Feedback</p>
                <button type="button" @click="copyLink" class="copy-btn" title="Copiar link do WhatsApp">
                  <span class="oficina-icon-copy"></span>
                </button>
              </div>
              <p class="qr-number">{{ formatPhoneNumber(whatsappNumber) }}</p>
            </div>
          </div>
        </div>
      `,
    },
  },
  data() {
    return {
      activeTab: 'whatsapp',
    }
  },
  computed: {
    visibleTabs() {
      if (!this.step.tabs) return []
      return this.step.tabs.filter(tab => {
        if (tab.showOnlyIfNotPresencial && this.config.platform === 'Presencial') {
          return false
        }
        return true
      })
    },
    currentTab() {
      return this.step.tabs?.find(tab => tab.id === this.activeTab) || {}
    },
    whatsappUrl() {
      if (!this.config.whatsappNumber) return ''
      return getWhatsappUrl(this.config.whatsappNumber)
    },
    qrCodeUrl() {
      if (!this.whatsappUrl) return ''
      return getQRCodeUrl(this.whatsappUrl)
    },
    formattedWhatsapp() {
      if (!this.config.whatsappNumber) return 'Não informado'
      return formatPhoneNumber(this.config.whatsappNumber)
    },
  },
  mounted() {
    // Restaurar tab ativa se houver, senão usar a primeira tab visível
    if (this.activeTabs.technicalSetup) {
      this.activeTab = this.activeTabs.technicalSetup
    } else if (this.visibleTabs.length > 0) {
      this.activeTab = this.visibleTabs[0].id
    }
  },
  methods: {
    updateTab(tabId) {
      this.activeTab = tabId
      this.$emit('update-tab', 'technicalSetup', tabId)
    },
    getIconClass(icon) {
      const iconMap = {
        QrCode: 'icofont-qr-code',
        Video: 'icofont-video-cam',
        Users: 'icofont-users-alt-4',
        Settings: 'icofont-settings',
        Monitor: 'icofont-monitor',
      }
      return iconMap[icon] || 'icofont-settings'
    },
    async copyWhatsappLink() {
      if (!this.whatsappUrl) return
      const ok = await copyToClipboard(this.whatsappUrl)
      if (ok && this.$toast) {
        this.$toast.success('Link copiado!', 'Sucesso', {
          position: 'topRight',
        })
      }
    },
    async copyEvaluationLink() {
      if (!this.config.evaluationLink) return
      const ok = await copyToClipboard(this.config.evaluationLink)
      if (ok && this.$toast) {
        this.$toast.success('Link de avaliação copiado!', 'Copiado', {
          position: 'topRight',
        })
      }
    },
    async copySosLink() {
      if (!this.config.sosLink) return
      const ok = await copyToClipboard(this.config.sosLink)
      if (ok && this.$toast) {
        this.$toast.success('Link SOS copiado!', 'Copiado', {
          position: 'topRight',
        })
      }
    },
  },
}
</script>

<style scoped>
.oficina-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 24px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.oficina-tabs::-webkit-scrollbar {
  height: 6px;
}

.oficina-tabs::-webkit-scrollbar-track {
  background: transparent;
}

.oficina-tabs::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.oficina-tabs::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.oficina-tab {
  padding: 12px 20px;
  border: 2px solid #e2e8f0;
  border-right: none;
  background: white;
  border-radius: 0;
  cursor: pointer;
  font-weight: 600;
  color: #64748b;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.oficina-tab:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.oficina-tab:last-child {
  border-right: 2px solid #e2e8f0;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.oficina-tab.active {
  background: #0FAFD7;
  border-color: #0FAFD7;
  color: white;
  z-index: 1;
  position: relative;
}

.oficina-tab.active + .oficina-tab {
  border-left-color: #0FAFD7;
}

.oficina-tab-content {
  min-height: 300px;
}

.oficina-slide-text {
  font-size: 16px;
  line-height: 1.6;
  color: #1e293b;
  margin-bottom: 16px;
}

.oficina-slide-subtext {
  font-size: 14px;
  color: #64748b;
  margin-top: 16px;
  font-style: italic;
}

.oficina-slide-tip {
  background: #eff6ff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
  margin-top: 24px;
  color: #1e40af;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.qr-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.oficina-qr-code {
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
}

.oficina-qr-code img {
  width: 200px;
  height: 200px;
  display: block;
}

.qr-info {
  margin-top: 20px;
  text-align: center;
}

.qr-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 4px;
}

.qr-header p {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.copy-btn {
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #64748b;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 14px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 28px;
  flex-shrink: 0;
}

.copy-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #0FAFD7;
}

.copy-btn:active {
  transform: scale(0.95);
}

.qr-number {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.tech-setup-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tech-setup-field label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.tech-setup-field-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tech-setup-field-value {
  flex: 1;
  font-size: 14px;
  color: #0f172a;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tech-setup-link-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #0FAFD7;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tech-setup-link-btn:hover {
  background: #0FAFD7;
  color: #ffffff;
  border-color: #0FAFD7;
}

.oficina-checklist {
  list-style: none;
  padding: 0;
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.oficina-checklist li {
  padding-left: 24px;
  position: relative;
  font-size: 15px;
  line-height: 1.6;
  color: #475569;
}

.oficina-checklist li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #0FAFD7;
  font-weight: bold;
  font-size: 18px;
}

.tech-setup-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 24px 0;
}

.tech-setup-intro-text {
  font-size: 15px;
  color: #64748b;
  margin: 0 0 20px 0;
  line-height: 1.6;
}

.tech-setup-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tech-setup-card-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 20px;
}

.tech-setup-card-title {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.tech-setup-card-text {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
}

.tech-setup-link-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.tech-setup-link-value {
  flex: 1;
  font-size: 14px;
  color: #0f172a;
  padding: 8px 12px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Ícone de copiar (CSS puro) */
.oficina-icon-copy {
  display: inline-block;
  width: 16px;
  height: 16px;
  position: relative;
  flex-shrink: 0;
  color: inherit;
}

.oficina-icon-copy::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 10px;
  height: 12px;
  border: 1.5px solid currentColor;
  border-radius: 2px;
  background: white;
  box-sizing: border-box;
}

.oficina-icon-copy::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 10px;
  height: 12px;
  border: 1.5px solid currentColor;
  border-radius: 2px;
  background: white;
  transform: translate(4px, 4px);
  opacity: 0.7;
  box-sizing: border-box;
}
</style>
